import { NOT_OBSERVED, type EvaluatedPersonDto, type ResponseValue } from '@genoma/shared';

import { useTranslate } from '../../i18n/index.tsx';

type Props = {
  people: EvaluatedPersonDto[];
  valueFor: (personId: string) => ResponseValue | undefined;
  onChange: (personId: string, value: ResponseValue) => void;
};

/**
 * The multiple format: one behavior read once, then everyone placed on it.
 *
 * Seeing the group together is the point — it is far easier to calibrate five
 * people against one another than to rate each in isolation. Six per screen is
 * the ceiling at which the anchors and the group still fit together.
 */
export const PeopleGrid = ({ people, valueFor, onChange }: Props) => {
  const t = useTranslate();
  const shortLabels: Record<number, string> = {
    1: t('scale.step1'),
    3: t('scale.step3'),
    5: t('scale.step5'),
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="min-w-[520px]">
        <div className="grid grid-cols-[1fr_repeat(6,44px)] items-end gap-1 pb-1.5">
          <div />
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="text-center">
              <div className="font-mono text-[13px] font-bold text-soft">{step}</div>
              {/* Steps 2 and 4 have no label of their own; the empty line keeps
                  every column on the same baseline instead of staggering them. */}
              <span className="block h-3 font-mono text-[8.5px] tracking-[0.04em] text-faint uppercase">
                {shortLabels[step] ?? '\u00a0'}
              </span>
            </div>
          ))}
          <div className="text-center">
            <div className="font-mono text-[10px] font-bold text-soft">N/A</div>
            <span className="block h-3" />
          </div>
        </div>

        {people.map((person) => {
          const value = valueFor(person.id);
          return (
            <div
              key={person.id}
              className="grid grid-cols-[1fr_repeat(6,44px)] items-center gap-1 border-t border-line-soft py-2"
              role="radiogroup"
              aria-label={t('scale.prompt', { name: person.fullName })}
            >
              <div className="flex min-w-0 items-center gap-2.5 pr-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#2AA0A0] to-teal-deep text-[13px] font-bold text-white">
                  {person.initials}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[14px] font-bold">
                    {person.fullName}
                  </span>
                  <span className="block truncate text-[12px] text-soft">
                    {person.jobTitle ?? t(`roleLevels.${person.roleLevel}`)}
                  </span>
                </span>
              </div>

              {[1, 2, 3, 4, 5].map((step) => (
                <button
                  key={step}
                  type="button"
                  role="radio"
                  aria-checked={value === step}
                  aria-label={`${person.fullName}: ${step}`}
                  onClick={() => onChange(person.id, step as ResponseValue)}
                  className={[
                    'mx-auto grid size-9 cursor-pointer place-items-center rounded-full border-2 font-mono text-[13px] font-bold transition',
                    value === step
                      ? 'border-teal bg-teal text-white shadow-selected'
                      : // 2 and 4 read as steps between the described ones, so
                        // they sit visually quieter than 1, 3 and 5.
                        step === 2 || step === 4
                        ? 'border-line-soft bg-white text-faint hover:border-teal hover:text-teal'
                        : 'border-line bg-white text-soft hover:border-teal hover:text-teal',
                  ].join(' ')}
                >
                  {step}
                </button>
              ))}

              <button
                type="button"
                role="radio"
                aria-checked={value === NOT_OBSERVED}
                aria-label={`${person.fullName}: ${t('scale.notObserved')}`}
                onClick={() => onChange(person.id, NOT_OBSERVED)}
                className={[
                  'mx-auto grid h-9 w-11 cursor-pointer place-items-center rounded-full border font-mono text-[10px] font-bold transition',
                  value === NOT_OBSERVED
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-white text-faint hover:border-soft',
                ].join(' ')}
              >
                N/A
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
